import type { PublicMaterial } from '@/services/materials';
import { resolveColorHex, isLightColor } from '@/utils/colors';

interface ColorSwatchProps {
  readonly materials: readonly PublicMaterial[];
  readonly selectedMaterialId: string;
  readonly selectedColor: string | null;
  readonly onSelectColor: (color: string) => void;
}

export const ColorSwatch = ({
  materials,
  selectedMaterialId,
  selectedColor,
  onSelectColor,
}: ColorSwatchProps) => {
  const material = materials.find((m) => m.materialId === selectedMaterialId);
  if (!material) return null;

  const availableColors = material.colorOptions.filter(
    (c) => material.colorStock[c] !== false,
  );

  if (availableColors.length === 0) {
    return (
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
        No colors currently in stock for this material.
      </p>
    );
  }

  return (
    <div className="flex" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {availableColors.map((color) => {
        const isSelected = selectedColor === color;
        const hex = resolveColorHex(color);
        const light = isLightColor(hex);

        return (
          <button
            key={color}
            type="button"
            onClick={() => onSelectColor(color)}
            className="focus-ring"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              border: `2px solid ${isSelected ? 'var(--color-primary-500)' : 'transparent'}`,
              backgroundColor: isSelected ? 'var(--bg-accent-subtle)' : 'transparent',
              cursor: 'pointer',
              transition: 'all var(--duration-fast) var(--ease-out)',
              minWidth: '4rem',
            }}
          >
            <div
              style={{
                width: '2.25rem',
                height: '2.25rem',
                borderRadius: 'var(--radius-full)',
                backgroundColor: hex,
                border: light ? '1px solid var(--border-default)' : 'none',
                boxShadow: isSelected
                  ? `0 0 0 2px var(--bg-surface), 0 0 0 4px var(--color-primary-400)`
                  : 'var(--shadow-sm)',
                transition: 'box-shadow var(--duration-fast) var(--ease-out)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: isSelected ? 'var(--font-weight-semibold)' : 'var(--font-weight-regular)',
                color: isSelected ? 'var(--text-accent)' : 'var(--text-secondary)',
                textTransform: 'capitalize',
              }}
            >
              {color}
            </span>
          </button>
        );
      })}
    </div>
  );
};
